import type { FunctionArgs, FunctionReturnType } from 'convex/server';

import type { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { brandFolderHref, brandHref } from '@/lib/branding/brand-navigation';

export type NativeBrandFolder = FunctionReturnType<
  typeof api.vfs.public.listDirectoriesByEntityPaginated
>['page'][number];
export type NativeBrandReference = FunctionReturnType<
  typeof api.branding.discovery.listFolderReferences
>[number];

/** Combine authorized identities with display-index rows, which can lag creation. */
export function mergeNativeBrandFolders(
  indexed: readonly NativeBrandFolder[],
  references: readonly NativeBrandReference[]
): NativeBrandFolder[] {
  const folders = new Map(indexed.map((entry) => [entry.path, entry]));
  for (const reference of references) {
    const existing = folders.get(reference.vfsPath);
    folders.set(reference.vfsPath, {
      ...existing,
      kind: 'directory',
      source: 'directory',
      entity: 'branding',
      name: reference.title,
      displayName: reference.displayName,
      path: reference.vfsPath,
      createdAt: existing?.createdAt ?? reference._creationTime,
      nodeReference: {
        version: 1,
        kind: 'directory',
        vfsNodeId: reference.rootNodeId,
      },
    });
  }
  return [...folders.values()];
}

export type LegacyBrand = FunctionReturnType<
  typeof api.branding.queries.listByOrganization
>[number];
export type AccessibleDrive = FunctionReturnType<
  typeof api.drives.queries.listAccessibleDrives
>[number];
export type CreateBrandFolderArgs = FunctionArgs<
  typeof api.branding.vfs.queries.createBrandFolder
>;

export type BrandDestination =
  | { kind: 'home' }
  | {
      kind: 'drive';
      rootNodeId: Id<'vfsNodes'>;
      name: string;
    };

export function nativeBrandDisplayName(entry: NativeBrandFolder): string {
  return entry.displayName?.trim() || entry.name;
}

export function legacyBrandDisplayName(
  brand: LegacyBrand,
  untitled: string
): string {
  const title = brand.title?.trim();
  if (title) return title;
  try {
    return new URL(brand.url).hostname.replace(/^www\./, '');
  } catch {
    return brand.url?.trim() || untitled;
  }
}

function searchable(values: ReadonlyArray<unknown>): string {
  return values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLocaleLowerCase();
}

export function filterNativeBrands(
  entries: readonly NativeBrandFolder[],
  query: string
): NativeBrandFolder[] {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return [...entries];
  return entries.filter((entry) =>
    searchable([
      entry.name,
      entry.displayName,
      entry.displayDescription,
    ]).includes(normalized)
  );
}

export function filterLegacyBrands(
  entries: readonly LegacyBrand[],
  query: string,
  untitled: string
): LegacyBrand[] {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return [...entries];
  return entries.filter((brand) =>
    searchable([
      legacyBrandDisplayName(brand, untitled),
      brand.url,
      brand.colors,
      brand.fonts,
      brand.brandInsights?.brandOverview,
      brand.brandInsights?.brandValues,
      brand.brandInsights?.brandToneOfVoice,
      brand.brandInsights?.brandAesthetics,
    ]).includes(normalized)
  );
}

export function accessibleBrandDestinations(
  drives: readonly AccessibleDrive[]
): BrandDestination[] {
  return [
    { kind: 'home' },
    ...drives.flatMap((drive) =>
      drive.rootNodeId && !drive.controlPlaneOnly && drive.status === 'active'
        ? [
            {
              kind: 'drive' as const,
              rootNodeId: drive.rootNodeId,
              name: drive.name,
            },
          ]
        : []
    ),
  ];
}

export function resolveBrandDestination(
  destinations: readonly BrandDestination[],
  destinationKey: string
): BrandDestination | null {
  return (
    destinations.find((destination) =>
      destination.kind === 'home'
        ? destinationKey === 'home'
        : String(destination.rootNodeId) === destinationKey
    ) ?? null
  );
}

export function buildCreateBrandFolderArgs(args: {
  orgSlug: string;
  name: string;
  destination: BrandDestination;
}): CreateBrandFolderArgs {
  return {
    orgSlug: args.orgSlug,
    name: args.name.trim(),
    ...(args.destination.kind === 'drive'
      ? { destinationParentNodeId: args.destination.rootNodeId }
      : {}),
  };
}

export function nativeBrandHref(
  orgSlug: string,
  entry: NativeBrandFolder
): string {
  // Shared with voice navigation so both open the same brand folder URL.
  return brandFolderHref(orgSlug, entry.path, entry.nodeReference);
}

export function legacyBrandHref(orgSlug: string, id: string): string {
  return brandHref(orgSlug, { brandingId: id });
}
